import { FC, useEffect, useRef, useState } from "react";

export interface JourneyStepOption {
  id: number;
  name: string;
  depth: number;
  hasChildren: boolean;
  description?: string;
  preferred_nomenclature?: string;
  is_mandatory?: boolean;
  has_dependency?: boolean;
}

interface Props {
  options: JourneyStepOption[];
  value: number;
  onChange: (stepId: number) => void;
}

const JourneyStepSelect: FC<Props> = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selected = options.find((s) => s.id === value) || null;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (id: number) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="form-control text-start d-flex align-items-center"
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: "pointer", paddingRight: "2rem" }}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selected ? "var(--default-text-color)" : "var(--bs-secondary-color, #6c757d)",
          }}
        >
          {selected ? selected.name : "Select journey step…"}
        </span>
        <i
          className="bi bi-chevron-down"
          style={{
            position: "absolute",
            right: "0.75rem",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "0.75rem",
            color: "var(--bs-secondary-color, #6c757d)",
            pointerEvents: "none",
          }}
        />
      </button>

      {open && (
        <div
          className="bg-white shadow"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid var(--input-border)",
            borderRadius: "0.35rem",
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.id === value;
            return (
              <div
                key={opt.id}
                onClick={() => handleSelect(opt.id)}
                style={{
                  padding: "0.375rem 0.75rem",
                  paddingLeft: `${12 + opt.depth * 20}px`,
                  fontWeight: opt.hasChildren ? 600 : 400,
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  cursor: "pointer",
                  backgroundColor: isSelected ? "var(--primary02, #efeaff)" : "transparent",
                  color: isSelected ? "var(--primary-color, #845adf)" : "var(--default-text-color)",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                {opt.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JourneyStepSelect;
